import { compact, isPlainObject, pick, pickBy } from 'lodash';

import { SERVER_FORMAT_DATE_LONG } from 'core/constants';
import { formatDateForServer } from 'core/utils';
import { defaultSpaAgreementFields } from 'quotes/constants';
import {
  IApplySelectedSpaAppliedLine,
  IApplySelectedSpaJobProgress,
  IApplySelectedSpaJobResult,
  IApplySelectedSpaOptions,
  IApplySelectedSpaSkippedLine,
  IApplySelectedSpaToLinesRequest,
  IApplySelectedSpaToLinesSource,
  ICustomerQuotePart,
  IDefaultSpaAgreementFormValue,
  IDefaultSpaAgreementPayload,
  ISelectedSpaCostSource,
  ISelectedSpaFormValue,
  ISelectedSpaPricesRequest,
  ITradeAgreement,
  ITradeAgreementLine,
} from 'quotes/types';

// Tag-shaped tokens only: the provider writes prose such as "qty < 10".
const HTML_TAG_PATTERN = /<\/?[a-z][^<>]*>/gi;
// Whatever the parser could still open as a tag, comment or declaration.
const TAG_OPENER_PATTERN = /<(?=[a-z/!?])/gi;

const stripMarkup = (text: string): string => {
  // Repeated: removing an inner tag can close the text around it into a new one ("<<b>a …>").
  const stripped = text.replace(HTML_TAG_PATTERN, '');

  if (stripped !== text) {
    return stripMarkup(stripped);
  }

  // A tag the pattern cannot see (a `<` or `>` inside a quoted attribute) still opens here.
  return stripped.replace(TAG_OPENER_PATTERN, '');
};

/**
 * Third-party text that reaches html-react-parser sinks, scrubbed at the one boundary it enters.
 * A bare `<` must survive: the code, name and Reg Number are posted back.
 */
export const sanitizeAgreementText = <T extends string | null | undefined>(value: T): T =>
  ('string' === typeof value ? stripMarkup(value) : value) as T;

export const mapTradeAgreementFromServer = (agreement: ITradeAgreement): ITradeAgreement => ({
  ...agreement,
  spaCostRegistrationNum: sanitizeAgreementText(agreement?.spaCostRegistrationNum),
  spaCostSource: sanitizeAgreementText(agreement?.spaCostSource),
  spaCostSourceName: sanitizeAgreementText(agreement?.spaCostSourceName),
  spaCostTaName: sanitizeAgreementText(agreement?.spaCostTaName),
});

/**
 * Every sink renders plain text; the strip only keeps stray markup out of the copy.
 */
const mapTradeAgreementLineMessage = (message: unknown): string | null =>
  'string' === typeof message ? sanitizeAgreementText(message).trim() || null : null;

export const mapTradeAgreementLinesFromServer = (
  lines: ITradeAgreementLine[]
): ITradeAgreementLine[] =>
  (lines ?? []).map((line) => ({
    ...line,
    message: mapTradeAgreementLineMessage(line?.message),
    tradeAgreements: (line?.tradeAgreements ?? []).map(mapTradeAgreementFromServer),
  }));

// Optional keys are omitted, not nulled: /quote/part/save posts null as '', save-bulk drops it.
export const mapSelectedSpaCostSource = (
  selectedSpa: ISelectedSpaFormValue
): ISelectedSpaCostSource => {
  const costSource: ISelectedSpaCostSource = {
    spaCostRegistrationNum: selectedSpa.spaCostRegistrationNum,
    spaCostSource: selectedSpa.spaCostSource,
  };

  const optionalFields = {
    spaCostSourceId: selectedSpa.spaCostSourceId,
    spaCostSourceName: selectedSpa.spaCostSourceName,
    // `|| null`: formatDateForServer answers a missing date with its empty placeholder.
    validSince: formatDateForServer(selectedSpa.spaCostStartDate, SERVER_FORMAT_DATE_LONG) || null,
    validUntil: formatDateForServer(selectedSpa.spaCostEndDate, SERVER_FORMAT_DATE_LONG) || null,
  };

  Object.entries(optionalFields).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      costSource[key] = value;
    }
  });

  return costSource;
};

/**
 * The BE keeps these only on a line storing none yet, and ignores the set without a code.
 */
export const mapDefaultSpaAgreementPayload = (
  defaultSpaAgreement?: IDefaultSpaAgreementFormValue | null,
  uom?: string | null
): IDefaultSpaAgreementPayload => {
  if (!defaultSpaAgreement?.externalSpaCostSource) {
    return {};
  }

  const payload: IDefaultSpaAgreementPayload = pickBy(
    pick(defaultSpaAgreement, defaultSpaAgreementFields),
    (value) => value !== null && value !== undefined && '' !== value
  );
  const costUom = defaultSpaAgreement.externalSpaCostUom;

  // Answered in another unit than the line's: the cost would be stored in the wrong unit.
  if (costUom && uom && costUom !== uom) {
    delete payload.externalSpaCost;
  }

  return payload;
};

// The save's keys and values, so the BE directs the preview's pricing call at the pick.
export const mapSelectedSpaPricesRequest = (
  selectedSpa?: ISelectedSpaFormValue | null
): ISelectedSpaPricesRequest =>
  selectedSpa?.spaCostSource
    ? { isCostingSelectedSpa: 1, costSource: mapSelectedSpaCostSource(selectedSpa) }
    : {};

// The version key is optional on the wire: omitted, the BE uses the one the source line stores.
export const mapApplySelectedSpaToLinesRequest = (
  { quoteId, sourceQuotePartId, spaCostSource, spaCostSourceId }: IApplySelectedSpaToLinesSource,
  { replaceManualCosts, replaceSelectedSpas }: IApplySelectedSpaOptions
): IApplySelectedSpaToLinesRequest => ({
  quoteId,
  replaceManualCosts: replaceManualCosts ? 1 : 0,
  replaceSelectedSpas: replaceSelectedSpas ? 1 : 0,
  sourceQuotePartId,
  spaCostSource,
  ...(spaCostSourceId ? { spaCostSourceId } : {}),
});

type TLooseRecord = Record<string, unknown>;

const asRecord = (value: unknown): TLooseRecord =>
  isPlainObject(value) ? (value as TLooseRecord) : {};

const asRecords = (value: unknown): TLooseRecord[] =>
  Array.isArray(value) ? value.map(asRecord) : [];

const toNullableString = (value: unknown): string | null => (null == value ? null : String(value));

const toNullableNumber = (value: unknown): number | null => {
  if (null == value || '' === value) {
    return null;
  }

  const number = Number(value);

  return Number.isFinite(number) ? number : null;
};

const toCount = (value: unknown, fallback: number): number => toNullableNumber(value) ?? fallback;

const toNullableFlag = (value: unknown): boolean | null =>
  null == value ? null : true === value || 'true' === value || 1 === Number(value);

// Both options or none: a half-read pair would report a choice the job never made.
const mapApplyOptions = (value: unknown): IApplySelectedSpaOptions | null => {
  const options = asRecord(value);
  const replaceManualCosts = toNullableFlag(options.replaceManualCosts);
  const replaceSelectedSpas = toNullableFlag(options.replaceSelectedSpas);

  return null === replaceManualCosts || null === replaceSelectedSpas
    ? null
    : { replaceManualCosts, replaceSelectedSpas };
};

// A line without an id is dropped: the id is its list key and its label when it has no number.
const mapAppliedLine = (line: TLooseRecord): IApplySelectedSpaAppliedLine | null => {
  const quotePartId = toNullableNumber(line.quotePartId);

  if (null === quotePartId) {
    return null;
  }

  return {
    lineNumber: toNullableString(line.lineNumber),
    newCost: toNullableNumber(line.newCost),
    previousCost: toNullableNumber(line.previousCost),
    quotePartId,
    spaCostSource: sanitizeAgreementText(toNullableString(line.spaCostSource) ?? ''),
    spaCostSourceId: toNullableString(line.spaCostSourceId),
  };
};

const mapSkippedLine = (line: TLooseRecord): IApplySelectedSpaSkippedLine | null => {
  const quotePartId = toNullableNumber(line.quotePartId);

  if (null === quotePartId) {
    return null;
  }

  return {
    lineNumber: toNullableString(line.lineNumber),
    message: mapTradeAgreementLineMessage(line.message),
    quotePartId,
    reason: toNullableString(line.reason) ?? '',
  };
};

/**
 * Every field is read explicitly: a blanket cast over the untyped JSON would hide a renamed key.
 */
export const mapApplySelectedSpaJobResult = (result: unknown): IApplySelectedSpaJobResult => {
  const record = asRecord(result);
  const totals = asRecord(record.totals);
  const applied = compact(asRecords(record.applied).map(mapAppliedLine));
  const skipped = compact(asRecords(record.skipped).map(mapSkippedLine));

  return {
    applied,
    options: mapApplyOptions(record.options),
    quoteParts: (Array.isArray(record.quoteParts) ? record.quoteParts : []) as ICustomerQuotePart[],
    skipped,
    spaCostSource: sanitizeAgreementText(toNullableString(record.spaCostSource)),
    spaCostSourceId: toNullableString(record.spaCostSourceId),
    totals: {
      applied: toCount(totals.applied, applied.length),
      lines: toCount(totals.lines, applied.length + skipped.length),
      skipped: toCount(totals.skipped, skipped.length),
    },
  };
};

/**
 * Null for any other shape: a job still `running` may already hold its final result.
 */
export const mapApplySelectedSpaJobProgress = (
  result: unknown
): IApplySelectedSpaJobProgress | null => {
  const progress = asRecord(asRecord(result).progress);
  const processed = toNullableNumber(progress.processed);
  const total = toNullableNumber(progress.total);

  if (null === processed || null === total) {
    return null;
  }

  return {
    applied: toCount(progress.applied, 0),
    processed,
    skipped: toCount(progress.skipped, 0),
    total,
  };
};
